<script setup lang="ts">
import backIcon from '@/assets/img/icon/ic-chevron-left.svg';
import menuIcon from '@/assets/img/icon/ic-menu.svg';
import { AuthAccountProfile } from '@/modules/auth/components';
import type { NavigationName } from '@/types/NavigationName';
import { NavigationNames } from '@/utils/constants/NavigationNames';
import { HORIZONTAL } from '@/utils/enums/NavigationNameDirections';
import { useRuntimeConfig } from 'nuxt/app';
import { AppLogo, AppTypography } from './base';
import { GlassButton } from './buttons';

const { showMobileNavbar = true } = defineProps<{
  showMobileNavbar?: boolean;
}>();

const config = useRuntimeConfig();
const route = useRoute();
const router = useRouter();
const slot = useSlots();

const navLabel = computed((): NavigationName => {
  const navName = route?.path?.split('/').pop();
  const navItem = NavigationNames.find((item) => item.value === navName);

  return navItem
    ? navItem
    : {
        value: '',
        title: '',
        direction: HORIZONTAL,
      };
});

const showSidebarButton = computed((): boolean => {
  return ['account'].includes(route.name as string);
});

const showAccountProfile = computed((): boolean => {
  return ['account'].includes(route.name as string);
});

const isAppendSlotEmpty = computed((): boolean => {
  return !slot['navbar-mobile-append'] && !showAccountProfile.value;
});

const goBack = (): void => {
  router.back();
};
</script>
<template>
  <div class="the-responsive-navbar">
    <v-app-bar elevation="0" height="72" class="navbar-desktop">
      <div class="navbar-desktop__border page-size d-flex align-center">
        <v-app-bar-nav-icon class="mr-2">
          <nuxt-link to="/" class="navbar-desktop__logo">
            <AppLogo width="48px" height="48px" class="mb-12" />
          </nuxt-link>
        </v-app-bar-nav-icon>
        <v-app-bar-title>
          <AppTypography type="body-2" class="navbar-desktop__title font-700">{{
            config.public.appName
          }}</AppTypography>
        </v-app-bar-title>
      </div>
    </v-app-bar>
    <div v-if="showMobileNavbar" class="navbar-mobile d-flex flex-column padding-lg">
      <div
        class="navbar-mobile__header justify-center"
        :class="{
          'navbar-mobile__header--horizontal': navLabel.direction === HORIZONTAL,
        }"
      >
        <div class="navbar-mobile__header__buttons d-flex">
          <v-img
            v-if="showSidebarButton"
            :src="menuIcon"
            width="24"
            max-width="24"
            contain
            class="navbar-mobile__header__buttons__menu"
          />
          <GlassButton v-else @click="goBack()">
            <v-img :src="backIcon" height="24" contain />
          </GlassButton>
        </div>
        <AppTypography
          :type="navLabel.direction === HORIZONTAL ? 'headline-1' : 'title-1'"
          class="navbar-mobile__header__title"
        >
          {{ navLabel.title }}
        </AppTypography>
      </div>
      <div
        class="navbar-mobile__append"
        :class="{
          'mt-2': !isAppendSlotEmpty,
        }"
      >
        <AuthAccountProfile v-if="showAccountProfile" />
        <slot name="navbar-mobile-append" />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.the-responsive-navbar {
  .navbar-desktop {
    background: transparent;
    box-shadow: none;
  }

  .navbar-mobile {
    @include mobile-nav-gradient();
    display: none !important;
    width: 100%;
    border-bottom-right-radius: 24px;
    border-bottom-left-radius: 24px;
    background-color: #9c9c9c54;

    &__header {
      position: relative;
      width: 100%;
      min-height: 52px;
      display: flex;
      align-items: center;

      &__buttons {
        position: absolute;
        left: 0;

        &__menu {
          cursor: pointer;
        }
      }
    }

    &__header:not(.navbar-mobile__header--horizontal) {
      align-items: start;
      flex-direction: column;

      .navbar-mobile__header__buttons {
        position: relative;
      }

      .navbar-mobile__header__title {
        margin-top: 10px;
      }
    }
  }

  @media (max-width: 768px) {
    .navbar-desktop {
      display: none;
    }

    .navbar-mobile {
      display: flex !important;
    }
  }
}
</style>
